import {Component, Input, EventEmitter, Output, OnInit} from "@angular/core";
import {AssetGqlModel} from "../asset/asset.gql-model";
import {AssetService} from "../asset/asset.service";
import {WorkflowService} from "../workflow/workflow.service";
import {AccessoryService} from "./accessory.service";
import {StandaloneAccessoryGqlModel} from "./standalone-accessory.gql-model";
import {SelectAccessorySoftwareDialogComponent} from "../asset/select-accessory-software-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";
import { BrokerApiContractAccountDataModel } from "../project/project.model";
import { SiteContactsModel } from "../workflow/site/assets-dropdowns-info-model";
import {BillFromValue} from "../bill-from/bill-from-with-region.model";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {acquisitionMethod} from "../types/simplify-constants";

@Component({
  selector: "edit-accessory",
  templateUrl: "./edit-accessory.template.pug",
})
export class EditAccessoryComponent implements OnInit {
  @Output() public emitAccessoriesChanges = new EventEmitter();
  @Output() public closeDropdown = new EventEmitter<void>();

  @Input() public asset: AssetGqlModel;
  @Input() public accessories: StandaloneAccessoryGqlModel[] = [];
  @Input() public individual: boolean;
  @Input() public enableMultipleCA: boolean;
  @Input() public siteContract: SiteContactsModel[];
  @Input() public itsmContractAccount: BrokerApiContractAccountDataModel[];
  @Input() public billFromAccounts: BillFromValue[];
  @Input() public projectId: string;


  public originalAccessories: StandaloneAccessoryGqlModel[] = [];
  public accessoriesSaved: boolean = false;
  public processingFlag: boolean = false;
  public changeMade: boolean = false;
  POR165_TMC: boolean;
  POR165: boolean;
  enablePor165: boolean;
  isMigrationProject = false;
  acquisitionMethod = acquisitionMethod;

  constructor(
    private dialogService: DialogService,
    public assetService: AssetService,
    private accessoryService: AccessoryService,
    private workflowService: WorkflowService,
    private confirmDialogService: ConfirmDialogService,
    private appSettingsService: AppSettingsService,
  ) {}

  async ngOnInit() {
    this.originalAccessories = this.buildOriginalAccessories(this.accessories);
    this.POR165_TMC = await this.appSettingsService.getFeaturePermission(
      "POR165_TMC",
      this.projectId,
    );
    this.POR165 = await this.appSettingsService.getFeaturePermission(
      "POR165",
      this.projectId,
    );
    this.enablePor165 = this.POR165 || this.POR165_TMC;
    this.isMigrationTargetSystem();
  }

  buildOriginalAccessories(accessories: StandaloneAccessoryGqlModel[]) {
    const clonedAccessories = [];
    accessories.forEach((acc) => {
      if (acc.changed) acc.changed = false;
      const clone = {...acc};
      clonedAccessories.push(clone);
    });
    return clonedAccessories;
  }

  private isMigrationTargetSystem() {
    const targetSystem = this.workflowService.workflowConfig.targetSystem;
    if (targetSystem === "midmps" || targetSystem === "mimc" ||
    targetSystem === "momc" || targetSystem === "mimp") {
      this.isMigrationProject = true;
    } else {
      this.isMigrationProject = false;
    }
  }

  buildAccessory(accessory: any) {
    return new StandaloneAccessoryGqlModel({
      assetId: this.asset.id,
      building: accessory.building,
      deviceId: accessory.deviceId,
      floor: accessory.floor,
      id: accessory.id,
      orderDate: accessory.orderDate,
      orderNumber: accessory.orderNumber,
      productDescription: accessory.productDescription,
      productNumber: accessory.productNumber,
      serialNumber: accessory.serialNumber,
      siteId: accessory.siteId,
      installDate: accessory.installDate,
      deviceEndDate: accessory.deviceEndDate,
      acquisitionMethod: accessory.acquisitionMethod || "",
    });
  }

  async changeDetected(field: string, index: number) {
    if (this.originalAccessories[index][field] !== this.accessories[index][field]) {
      this.accessoriesSaved = false;
      this.changeMade = true;
      this.accessories[index].changed = true;
      this.emitAccessoriesChanges.emit({
        accessories: this.accessories,
        changes: true,
      });
    }
  }

  async associateAccessories() {
    const selectedAccessories = await this.dialogService.showDialog(
      SelectAccessorySoftwareDialogComponent,
      {
        typeModal: "accessories",
        siteId: this.asset.siteId,
        deviceName: this.asset.modelName,
        assetId: this.asset.id,
      },
    );
    if (selectedAccessories.result === "success" && selectedAccessories.solutions.length) {
      selectedAccessories.solutions.forEach((acc) => {
        const accessory = this.buildAccessory(acc);
        this.accessories.push(accessory);
        this.originalAccessories.push(new StandaloneAccessoryGqlModel(accessory));
      });

      this.emitAccessoriesChanges.emit({
        accessories: this.accessories,
        changes: this.changeMade,
      });
    }
  }

  async disassociate(index: number) {
    const clonedAccessory = new StandaloneAccessoryGqlModel(this.accessories[index]);
    const confirm = await this.confirmDialogService.confirm(
      "Confirm",
      `Are you sure you want to disassociate the accessory (${clonedAccessory.productDescription}) from this device (${this.asset.modelName})?`,
    );
    if (confirm) {
      clonedAccessory.assetId = null;
      const {updated} = await this.accessoryService.saveAccessory(clonedAccessory);
      if (updated) {
        this.accessories[index].assetId = null;
        this.accessories = this.accessories.filter(acc => acc.assetId);
        this.originalAccessories[index].assetId = null;
        this.originalAccessories = this.originalAccessories.filter(acc => acc.assetId);
        this.emitAccessoriesChanges.emit({
          accessories: this.accessories,
          changes: this.changeMade,
        });
      }
    }
  }

  accessoryValidations() {[];
    if (this.billFromAccounts && !this.accessories[0].billFromBPIDId) {
      throw new Error("You must select a Bill-From BPID for this accessory.");
    }
  }

  async saveAccessories() {
    this.processingFlag = true;
    if (!this.changeMade) return;
    let success;
    if (this.accessories[0].serialNumber) {
      this.accessories[0].serialNumber = this.accessories[0].serialNumber.toUpperCase().trim();
    }
    if (this.individual) {
      try {
        const {updated} = await this.accessoryService.saveAccessory(this.accessories[0]);
        success = updated;
      } catch (e) {
        this.emitAccessoriesChanges.emit({
          accessories: this.accessories,
          changes: false,
          errors: e.message.replace("GraphQL error: ", ""),
        });
      }
    } else {
      for (let i = 6; i <= 10; i++) this.asset.formGroup.controls[`custom${i}`].markAsDirty();
      const {updated} = await this.assetService.saveAsset(this.asset);
      success = updated;
    }
    if (success) {
      this.accessoriesSaved = true;
      this.originalAccessories = [];
      this.originalAccessories = this.buildOriginalAccessories(this.accessories);
      this.emitAccessoriesChanges.emit({
        accessories: this.accessories,
        changes: false,
        accessoriesSaved: this.accessoriesSaved,
      });
      this.closeDropdown.emit();
    }
    this.processingFlag = false;
  }

  async removeAccessory() {
    this.accessories[0].keep = false;
    this.emitAccessoriesChanges.emit({
      accessories: this.accessories,
      changes: false,
    });
  }

  dateFormat(field: string) {
    if (!this.accessories[field]) return;
    if (this.accessories[field].length === 4 || this.accessories[field].length === 7) {
      this.accessories[field] = `${this.accessories[field]}-`;
    }
  }
  getBPID(index) {
    let bpid;
    const found = this.billFromAccounts.find(bf => bf.id === this.accessories[index].billFromBPIDId);
    if (this.billFromAccounts && found) {
      bpid = `BPID: ${found.value.billFromAccBPID}`;
    }
    return bpid;
  }
}
